import type { Currency } from '../constants/currencies';
import type { BaseDocument } from '../mongo/base-document.interface';

// ─── Billing plan configuration ─────────────────────────────────────────────

export interface BillingPlanConstraints {
  vehicleMaxCount?: number;
}

export interface BillingPlanFees {
  monthlyFee?: number;
  monthlyVehicleFee?: number;
  taskFee?: number;
}

export interface BillingPlanLimits {
  monthlyPriceCap?: number;
}

export interface BillingPlanConfig {
  constraints: BillingPlanConstraints;
  fees: BillingPlanFees;
  limits: BillingPlanLimits;
}

export interface BillingPlanConfigOverrides {
  constraints?: Partial<BillingPlanConstraints>;
  fees?: Partial<BillingPlanFees>;
  limits?: Partial<BillingPlanLimits>;
}

// ─── Billing plan ────────────────────────────────────────────────────────────

export interface BillingPlan extends BaseDocument {
  configuration: BillingPlanConfig;
  currency: Currency;
  name: string;
}

export type CreateBillingPlan = Omit<BillingPlan, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

/** Full replace of every field, including `name` — the plan id is the immutable route key. */
export type UpdateBillingPlan = CreateBillingPlan;

/** `inUse` is computed, never persisted. */
export type BillingPlanView = BillingPlan & { inUse: boolean };

/** Immutable creation-time snapshot of a plan, embedded (denormalized) into a `TenantBillingConfiguration`. */
export type BillingPlanSummary = Pick<BillingPlan, 'configuration' | 'currency' | 'id' | 'name'>;

// ─── Billing settings (global, singleton) ───────────────────────────────────

export const DEFAULT_FREE_TRIAL_DURATION_IN_DAYS = 30;

export interface BillingSettings extends BaseDocument {
  freeTrialDurationInDays: number;
}

export type CreateBillingSettings = Omit<BillingSettings, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

/** The full singleton document is an implementation detail. */
export type BillingSettingsView = Pick<BillingSettings, 'freeTrialDurationInDays'>;

// ─── Tenant billing configuration ───────────────────────────────────────────

export const TENANT_BILLING_CONFIGURATION_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  UPCOMING: 'upcoming',
} as const;
export type TenantBillingConfigurationStatus =
  (typeof TENANT_BILLING_CONFIGURATION_STATUS)[keyof typeof TENANT_BILLING_CONFIGURATION_STATUS];

/**
 * A tenant's free trial — `durationDays` is a snapshot of `BillingSettings.freeTrialDurationInDays`
 * taken the moment the trial started, so a later setting change never affects an ongoing trial.
 */
export interface TenantFreeTrial {
  durationDays: number;
  startedAt: Date;
}

export interface TenantBillingConfiguration extends BaseDocument {
  from: Date;
  overrides: BillingPlanConfigOverrides;
  /** Immutable snapshot of the referenced plan, taken at creation time. `null` = no plan chosen yet (plan-less trial row). */
  plan: BillingPlanSummary | null;
  status: TenantBillingConfigurationStatus;
  /** Absent/null = still active (open-ended). */
  to?: Date | null;
  /** Absent/null = no trial recorded. While `plan` is still `null`, `from` is the planned trial end (`trial.startedAt + trial.durationDays`). */
  trial?: TenantFreeTrial | null;
}

/** `status` and `plan` are always server-resolved, never client-supplied. `trial` is only accepted for the legacy manual-creation flow. */
export type CreateTenantBillingConfiguration = Omit<
  TenantBillingConfiguration,
  'createdAt' | 'deletedAt' | 'id' | 'plan' | 'status' | 'tenantId' | 'updatedAt'
> & { planId: string };

export type UpdateTenantBillingConfigurationStatus = { status: TenantBillingConfigurationStatus };

/** Service-level read shape for a tenant's current/latest billing configuration view. */
export interface TenantBillingConfigurationView {
  configuration: TenantBillingConfiguration;
  /** `null` while `configuration.plan` is `null` (plan-less trial row) — there is no base config to merge overrides onto yet. */
  effectiveConfig: BillingPlanConfig | null;
  /** The embedded plan snapshot — `null` while the trial has not been converted to a plan yet. */
  plan: BillingPlanSummary | null;
}

// ─── Bill ────────────────────────────────────────────────────────────────────

export interface BillMetrics {
  /** Planned metric — count of distinct active vehicles over the period. Not computed yet; always 0. */
  activeVehicleCount: number;
  /** Count of billable tasks terminated during the period. */
  taskCount: number;
  /** Planned metric — cumulative active-vehicle days over the period. Not computed yet; always 0. */
  vehicleActivityDays: number;
}

export const BILL_LINE_KEYS = {
  FREE_TRIAL_ADJUSTMENT: 'free-trial-adjustment',
  MONTHLY_FEE: 'monthly-fee',
  PRICE_CAP_ADJUSTMENT: 'price-cap-adjustment',
  TASKS: 'tasks',
  VEHICLES: 'vehicles',
} as const;
export type BillLineKey = (typeof BILL_LINE_KEYS)[keyof typeof BILL_LINE_KEYS];

export interface BillLine {
  amount: number;
  key: BillLineKey;
  quantity?: number;
  unitPrice?: number;
}

export const BILLING_CONSTRAINTS = { VEHICLE_MAX_COUNT: 'vehicleMaxCount' } as const;
export type BillingConstraint = (typeof BILLING_CONSTRAINTS)[keyof typeof BILLING_CONSTRAINTS];

export interface BillingViolation {
  actual: number;
  constraint: BillingConstraint;
  limit: number;
}

export interface BillingPeriod {
  /** 1-12 */
  month: number;
  year: number;
}

export interface Bill extends BaseDocument {
  billingPeriod: BillingPeriod;
  computedAt: Date;
  /** Snapshot of the referenced plan's currency at calculation time. */
  currency: Currency;
  lines: BillLine[];
  /** Whether this bill is final (timer-triggered, immutable) or a simulation (on-demand, overwritable). */
  locked: boolean;
  metrics: BillMetrics;
  planName: string;
  total: number;
  violations: BillingViolation[];
}

export type CreateBill = Omit<Bill, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

// ─── Billing metrics (usage counters feeding BillMetrics) ───────────────────

export interface BillingMetrics extends BaseDocument {
  month: number;
  taskCount: number;
  year: number;
}

export type CreateBillingMetrics = Omit<BillingMetrics, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

// ─── Counted-task ledger (idempotent task counting) ─────────────────────────

/** One ledger row per task ever counted toward `BillingMetrics.taskCount` — the insert is the idempotency check. */
export interface BillingCountedTask extends BaseDocument {
  /** When the task was completed — the date billing buckets and trial-exclusion windows key off. */
  completedAt: Date;
  countedAt: Date;
  month: number;
  taskId: string;
  year: number;
}

export type CreateBillingCountedTask = Omit<
  BillingCountedTask,
  'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'
>;
